import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import "./Home.css";
import StatCard from "../components/statCard/StatCard";
import TabSelector from "../components/tabSelector/TabSelector";
import EventCard from "../components/eventCard/EventCard";
import Header from "../components/header/Header";
import { CalendarIcon } from "../helper/icon";
import StatCards from "../components/statCard/StatCard";
import { AuthContext } from "../context/auth";
import { useLocation, useNavigate } from "react-router-dom";
import { formatDateWithOrdinal } from "../helper/utils";
import NoEventDisplay from "../components/noEventDisplay/NoEventDisplay";
import { getEventStatus, sortEventsByDate } from "../helper/utils";
import EventCardSkeleton from "../components/skeletons/EventCardSkeletons";

const Home = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [activeTab, setActiveTab] = useState("upcoming");
  const [guestFilter, setGuestFilter] = useState("all");
  const [currentDate, setCurrentDate] = useState(new Date());
  const [events, setEvents] = useState({
    upcoming: [],
    past: [],
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [shouldReload, setShouldReload] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const perPage = isMobile ? 8 : 20;

  const { user } = useContext(AuthContext);
  const location = useLocation();
  const userName = location.state || {};
  const navigate = useNavigate();
  const userId = user?._id || location.state?.user?.userId;
  const role = user?.activeRole;

  const activeRole = user?.activeRole;
  const isHost = activeRole === "host";
  const isGuest = activeRole === "guest";

  const displayedEvents = events[activeTab] || [];

  const fetchEvents = async (userId, role, page = 1, perPage = 20) => {
    try {
      let url = "";
      let filter = activeTab === "past" ? "past" : "upcoming";

      if (role === "host") {
        url = `/events/user/${userId}`;
      } else if (role === "guest") {
        url = `/events/guest/${userId}`;
      } else if (role === "usher") {
        url = `/events/usher/${userId}`;
      }

      const response = await axios.get(url, {
        params: { page, perPage, filter },
      });

      if (!response.data.successStatus) return { events: [], hasMore: false };

      const source = response.data.data;

      return {
        events: source.events || [],
        hasMore: page < source.totalPages,
      };
    } catch (error) {
      setError("Failed to load events.");
      return { events: [], hasMore: false };
    }
  };

  useEffect(() => {
    const loadEvents = async () => {
      if (!userId) {
        setError("Please log in to view events.");
        setLoading(false);
        return;
      }
      setLoading(true);
      setPage(1);
      setHasMore(true);

      const data = await fetchEvents(userId, role, 1, perPage);

      setEvents((prev) => ({
        ...prev,
        [activeTab]: data.events || [],
      }));

      setHasMore(data.hasMore);
      setLoading(false);
    };

    loadEvents();
  }, [userId, role, activeTab, perPage]);

  useEffect(() => {
    if (isMobile) return;

    const handleScroll = () => {
      if (
        window.innerHeight + window.scrollY >=
          document.body.offsetHeight - 300 &&
        hasMore &&
        !isFetchingMore
      ) {
        loadMoreEvents();
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [isMobile, hasMore, isFetchingMore, displayedEvents]);

  const loadMoreEvents = async () => {
    if (!hasMore || isFetchingMore) return;

    setIsFetchingMore(true);
    const nextPage = page + 1;
    const data = await fetchEvents(userId, role, nextPage, perPage);

    setEvents((prev) => ({
      ...prev,
      [activeTab]: [...prev[activeTab], ...data.events],
    }));

    setHasMore(data.hasMore);
    setPage(nextPage);
    setIsFetchingMore(false);
  };

  useEffect(() => {
    if (location.state?.reloadHome) {
      setShouldReload(true);
    }
  }, [location.state]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    const dateUpdateInterval = setInterval(() => {
      setCurrentDate(new Date());
    }, 60000);
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      clearInterval(dateUpdateInterval);
    };
  }, []);

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
  };

  // const displayedEvents =
  //   activeTab === "upcoming"
  //     ? (events?.upcomingEvents ?? [])
  //     : (events?.pastEvents ?? []);

  const handleEventClick = (event) => {
    navigate(`/eventManagementDetails/${event.id || event._id}`, {
      state: {
        eventTitle: event.title || event.eventName || "Event",
        eventDate: event.eventDate || event.startDate,
        eventStatus: getEventStatus(event.eventDate || event.startDate),
        eventDetails: event,
        from: `/home/${role}`,
      },
    });
  };

  return (
    <div className="home-dashboard">
      <Header />
      {!loading &&
      role === "host" &&
      displayedEvents.length === 0 &&
      activeTab === "upcoming" ? (
        <NoEventDisplay imageType={isMobile ? "mobile" : "desktop"} />
      ) : (
        <div className="home-dashboard">
          <div className="home-dashboard-box">
            <div className="home-date-card">
              <div className="home-calendar-icon">
                <CalendarIcon />
              </div>
              <div className="home-date-info">
                <p>Today's Date</p>
                <h3>{formatDateWithOrdinal(currentDate)}</h3>
              </div>
            </div>
          </div>

          <div className="home-stats-grid"></div>

          <div className="home-events-section">
            <TabSelector
              tabs={[
                { id: "upcoming", label: "Upcoming Events" },
                !isHost && { id: "past", label: "Past Events" },
              ]}
              onTabChange={handleTabChange}
              showActive={false}
              withSearch={true}
              badgeCount={
                activeTab === "upcoming"
                  ? events?.upcomingEvents?.length || 0
                  : events?.pastEvents?.length || 0
              }
            />

            <div className="home-events-list">
              {loading ? (
                <>
                  {Array.from({ length: 4 }).map((_, index) => (
                    <EventCardSkeleton key={index} />
                  ))}
                </>
              ) : displayedEvents.length === 0 && activeTab === "upcoming" ? (
                <div className="home-no-events-message">
                  <h2 className="home-no-events-title">
                    {isHost
                      ? "Welcome to Your Dashboard"
                      : "Welcome to your event Invites"}
                  </h2>

                  <p className="home-no-events-description">
                    {isHost
                      ? "You don't have any upcoming events yet. Click the button below to create your first event."
                      : "You don't have any event invites yet. Check back later for new events."}
                  </p>

                  {isHost ? (
                    <button
                      className="home-create-event-button"
                      onClick={() => navigate("/createEvent")}
                    >
                      Create Your First Event
                    </button>
                  ) : (
                    <button
                      className="home-create-event-button"
                      onClick={() => navigate("/joinEvent")}
                    >
                      Join An Event
                    </button>
                  )}
                </div>
              ) : displayedEvents.length === 0 ? (
                <div>
                  No {activeTab === "upcoming" ? "upcoming" : "past"} events
                  found.
                </div>
              ) : (
                <>
                  {displayedEvents.map((event) => (
                    <EventCard
                      key={event._id || event.id}
                      id={event._id || event.id}
                      onPress={() => handleEventClick(event)}
                      date={formatDateWithOrdinal(
                        event.eventDate ||
                          event.startDate ||
                          event.multipleDates?.[0]?.date,
                      )}
                      title={
                        event.eventTitle || event.eventName || "Untitled Event"
                      }
                      type={event.eventType || "Unknown"}
                      time={
                        event.startTime ||
                        event.eventTime ||
                        new Date(
                          event.eventDate || event.startDate,
                        ).toLocaleTimeString([], {
                          hour: "2-digit",
                          minute: "2-digit",
                        })
                      }
                      location={
                        event.eventLandmark ||
                        event.eventLocation ||
                        event.multipleDates?.[0]?.location ||
                        event.customAddress ||
                        "Not specified"
                      }
                      attendees={
                        event.totalGuest ||
                        event.attendees ||
                        event.invitations?.length ||
                        0
                      }
                      image={
                        event.eventImages?.[0]?.uri || "/images/eventImage.jpg"
                      }
                      invitations={isHost ? event.invitations || [] : []}
                      isUsher={role === "usher"}
                      checkedInCount={event.checkedInCount || 0}
                      totalGuests={event.invitations?.length || 0}
                      approvalStatus={event.approvalStatus || null}
                      event={event}
                    />
                  ))}

                  {isMobile && hasMore && (
                    <div className="home-load-more-container">
                      <button
                        className="home-load-more-button"
                        onClick={loadMoreEvents}
                        disabled={isFetchingMore}
                      >
                        {isFetchingMore ? "Loading..." : "Load More"}
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Home;
